import { brand } from '../../../theme/themePrimitives';
import { createSelector } from 'reselect';
import CustomDropdown from '../../reusable/dropdown';
import CustomModal from '../../modals';
import LabelWrapper from '../../wrapper/label';
import PipelineApi from '../../../apis/pipeline';
import { pushLeadsToPipelineThunk } from '../../../redux/thunks/lead';
import { replaceToken } from '../../../redux/slices/index';
import { showError } from '../../../redux/slices/error';
import { AppDispatch, RootState } from '../../../redux/store';
import { Box, Button, SelectChangeEvent, Typography } from '@mui/material';
import { LeadObject, PushLeadToPipelineParams } from '../../../params/redux/leads';
import { PipelineObject, StageObject } from '../../../params/pipeline';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: ReviewProspect Component
 * @paragraph: This component is used to review selected prospects and optionally push them into a pipeline and stage.
 * It provides the ability to select a pipeline, a stage, and choose actions for unselected prospects.
 * @interface: ReviewProspectProps
 * @property: {boolean} open - Controls the visibility of the modal.
 * @property: {() => void} onClose - Function to close the modal.
 * @property: {number} totalCount - Total count of prospects available for review.
 * @property: {number[]} selectedRows - Array of selected prospect IDs.
 * @property: {() => void} setIsProspectOpen - Function to manage the state of the prospect view.
 */

interface ReviewProspectProps {
    open: boolean;
    onClose: () => void;
    selectedRows: number[];
}


const stateToken = (state: RootState) => state.app.token;
const statePipelineLoaders = (state: RootState) => state.pipeline.loaders;
const stateProspectLoaders = (state: RootState) => state.prospect.loaders;
const statePipelines = (state: RootState) => state.pipeline.pipelines;

const selectedState = createSelector(
    [stateToken, statePipelines, statePipelineLoaders, stateProspectLoaders],
    (token, pipelines, pipelineLoaders, prospectLoaders) => ({
        token,
        pipelines,
        pipelineLoaders,
        prospectLoaders
    })
);

/**
 * @heading2: ReviewProspect
 */
const PushToPipelineModal: React.FC<ReviewProspectProps> = ({
    open,
    onClose,
    selectedRows
}) => {
    const dispatch = useDispatch<AppDispatch>();
    // const [selectedAction, setSelectedAction] = useState<string>('');
    const { leads, selectedTab } = useSelector((state: RootState) => state.lead);
    const { selectedForm } = useSelector((state: RootState) => state.form);
    const [selectedPipeline, setSelectedPipeline] = useState<PipelineObject>();
    const [selectedStage, setSelectedStage] = useState<StageObject>();
    const { token, pipelines, pipelineLoaders, prospectLoaders } = useSelector(selectedState);

    const filteredLeads = leads.filter((lead: LeadObject) => lead.form_id === selectedForm?.id);

    /**
     * @heading3: Functions
     * @heading4: Handles the selection of a pipeline.
     * @paragraph: Fetches the pipeline details and updates the state.
     * @param: {SelectChangeEvent<unknown>} event - Event triggered when pipeline is selected.
     */
    const handlePipelineChange = async (event: SelectChangeEvent<unknown>) => {
        event.preventDefault();
        const target = event.target as HTMLInputElement;
        const pipelineId = parseInt(target.value as string, 10);
        try {
            const response = await PipelineApi.getSinglePipelineDetail(token, pipelineId);
            if (response.success) {
                dispatch(replaceToken(response.token));
                setSelectedPipeline(response?.pipeline as PipelineObject);
            } else {
                dispatch(showError({
                    title: 'Error',
                    description: response.msg
                }));
            }
        } catch (e) {
            // Catch errors
        }
    };

    /**
     * @heading4: Handles the selection of a stage.
     * @paragraph: Updates the selected stage in the component state.
     * @param: {SelectChangeEvent<unknown>} event - Event triggered when stage is selected.
     */
    const handleStageChange = (event: SelectChangeEvent<unknown>) => {
        const stageId = Number(event.target.value);
        setSelectedStage(selectedPipeline?.pipeline_stages?.find((stage) => stage.id === stageId));
    };

    const submitHandler = async () => {
            try {
                const data: PushLeadToPipelineParams = {
                    lead_result_id_array: selectedRows,
                    pipeline_id: selectedPipeline?.id as number,
                    stage_id: selectedStage?.id as number
                };
                await dispatch(pushLeadsToPipelineThunk(data)).unwrap();
                onClose();
            } catch (e) {
                // Catch errors
            }
    };

    return (
        <CustomModal title="Insert Leads in Pipeline" open={open} handleClose={prospectLoaders.isInserting ? () => null : onClose}>
            <Box display="flex" flexDirection="column" gap={2}>
                <Box textAlign="center">
                <Typography sx={{ background: brand[50], borderRadius: '4px', padding: '8px', color: brand[300] }}>
                    You have selected {selectedRows.length} out of {selectedTab === 0 ? leads.length : filteredLeads.length}
                </Typography>
            </Box>
            <Box display="flex" gap={2} width="100%" boxSizing="border-box">
                    <Box width="100%" boxSizing="border-box">
                        <LabelWrapper label="Pipeline" />
                        {pipelines.length !== 0 && <CustomDropdown
                            options={pipelines.map((pipe) => ({
                                label: pipe.name,
                                value: pipe.id
                            }))}
                            onChange={handlePipelineChange}
                            error={false}
                            value={selectedPipeline ? selectedPipeline.id : ''}
                            disabled={pipelineLoaders.isUpdating}
                        />}
                    </Box>
                    <Box width="100%" boxSizing="border-box">
                        <LabelWrapper label="Stage" />
                        <Box boxSizing="border-box">{
                            <CustomDropdown
                                options={
                                    selectedPipeline?.pipeline_stages
                                        ? selectedPipeline.pipeline_stages.map((pipe) => ({
                                            label: pipe.stage_name,
                                            value: pipe.id
                                        }))
                                        : []
                                }
                                onChange={handleStageChange}
                                error={false}
                                value={selectedStage ? selectedStage.id : ''}
                                disabled={pipelineLoaders.isUpdating}
                            />
                        }</Box>
                    </Box>
                </Box>
            <Box display="flex" alignItems="center" gap={2}>
                <Button onClick={onClose} variant="outlined" size="large" color="secondary" fullWidth disabled={prospectLoaders.isInserting}>
                    Cancel
                </Button>
                <Button
                    onClick={submitHandler}
                    variant="contained"
                    color="primary" size="large"
                    disabled={prospectLoaders.isInserting || !selectedPipeline || !selectedStage} fullWidth
                >
                    {prospectLoaders.isInserting ? 'Inserting...' : 'Insert Leads'}
                </Button>
            </Box>
            </Box>
        </CustomModal>
    );
};

export default PushToPipelineModal;
